import { getProductSourceInspectSourceIdFromPrefix } from '@/features/product-catalog/utils/productSourceInspectPath'
import {
  PRODUCT_LAW_KEYS,
  type ProductLawKey,
} from '@/shared/hooks/api/product'

export const V2_PRODUCTS_PATH = '/v2/products'
export const V2_COMPONENTS_PATH = '/v2/components'
export const V2_SUPPLIERS_PATH = '/v2/suppliers'
export const V2_SETTINGS_PATH = '/v2/settings'
export const V2_ORGS_ROLES_PATH = '/v2/orgs-roles'

export function getV2OrgsRolesDetailId(pathname: string): string | undefined {
  const prefix = `${V2_ORGS_ROLES_PATH}/`
  if (!pathname.startsWith(prefix)) {
    return undefined
  }
  const segment = pathname.slice(prefix.length).split('/')[0]
  if (segment === '') {
    return undefined
  }
  return segment
}

export function getV2ProductDetailPath(productId: string): string {
  return `${V2_PRODUCTS_PATH}/${productId}`
}

export function getV2ComponentDetailPath(componentId: string): string {
  return `${V2_COMPONENTS_PATH}/${componentId}`
}

export function getV2SupplierDetailPath(supplierId: string): string {
  return `${V2_SUPPLIERS_PATH}/${supplierId}`
}

export function getV2ProductPartsPath(productId: string): string {
  return `${getV2ProductDetailPath(productId)}/parts`
}

export function isV2ProductPartsPath(
  pathname: string,
  productId: string
): boolean {
  return pathname === getV2ProductPartsPath(productId)
}

export function getV2ProductSourceInspectPath(
  productId: string,
  sourceId: string
): string {
  return `${getV2ProductDetailPath(productId)}/sources/${sourceId}`
}

export function getV2ProductSourceInspectSourceId(
  pathname: string,
  productId: string
): string | undefined {
  return getProductSourceInspectSourceIdFromPrefix(
    pathname,
    `${getV2ProductDetailPath(productId)}/sources/`
  )
}

export function getV2ProductLawPath(
  productId: string,
  law: ProductLawKey
): string {
  return `${getV2ProductDetailPath(productId)}/${law}`
}

function isProductLawKey(value: string): value is ProductLawKey {
  return (PRODUCT_LAW_KEYS as readonly string[]).includes(value)
}

export function getV2ProductLawFromPath(
  pathname: string,
  productId: string
): ProductLawKey | undefined {
  const prefix = `${getV2ProductDetailPath(productId)}/`
  if (!pathname.startsWith(prefix)) {
    return undefined
  }

  const rest = pathname.slice(prefix.length)
  if (!isProductLawKey(rest)) {
    return undefined
  }

  return rest
}

export function isUnknownV2ProductNestedPath(
  pathname: string,
  productId: string
): boolean {
  const prefix = `${getV2ProductDetailPath(productId)}/`
  if (!pathname.startsWith(prefix)) {
    return false
  }

  if (isV2ProductPartsPath(pathname, productId)) {
    return false
  }

  if (getV2ProductSourceInspectSourceId(pathname, productId) != null) {
    return false
  }

  if (getV2ProductLawFromPath(pathname, productId) != null) {
    return false
  }

  return true
}
